import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = { name: "", contact_person: "", phone: "", email: "", address: "", gst_number: "", opening_balance: 0, status: "Active" };

export default function DebtorMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [outstanding, setOutstanding] = useState({});
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/customers").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/reports/customer-outstanding").then(r => setOutstanding(Object.fromEntries(r.data.map(x => [x.id, x.outstanding])))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...f,
        opening_balance: Number(f.opening_balance) || 0,
        contact_person: f.contact_person || null, email: f.email || null,
        address: f.address || null, gst_number: f.gst_number || null,
      };
      if (editId) await api.patch(`/customers/${editId}`, payload);
      else await api.post("/customers", payload);
      toast.success(editId ? "Debtor updated" : "Debtor added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (c) => {
    setF({ ...empty, ...Object.fromEntries(Object.keys(empty).map(k => [k, c[k] ?? empty[k]])) });
    setEditId(c.id); setOpen(true);
  };

  const del = async (id) => {
    if (!confirm("Delete this debtor record?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><UserCog className="w-3 h-3"/> Accounts & Finance · Debtors — balances reflect in Customer Ledger</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Debtor Master</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-debtor-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
              <Plus className="w-4 h-4 mr-1"/> New Debtor
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Debtor (Customer)</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3" onKeyDownCapture={handleSimpleFormNav}>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer Name *</Label>
                <Input data-testid="deb-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Contact Person</Label>
                <Input data-testid="deb-contact" value={f.contact_person} onChange={(e) => setF(x => ({ ...x, contact_person: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone Number *</Label>
                <Input data-testid="deb-phone" value={f.phone} onChange={(e) => setF(x => ({ ...x, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                <Input data-testid="deb-email" value={f.email} onChange={(e) => setF(x => ({ ...x, email: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GST Number</Label>
                <Input data-testid="deb-gst" value={f.gst_number} onChange={(e) => setF(x => ({ ...x, gst_number: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Opening Balance (₹ due)</Label>
                <Input data-testid="deb-opening" type="number" value={f.opening_balance} onChange={(e) => setF(x => ({ ...x, opening_balance: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                <Select value={f.status} onValueChange={(v) => setF(x => ({ ...x, status: v }))}>
                  <SelectTrigger data-testid="deb-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                <Textarea data-testid="deb-address" value={f.address} onChange={(e) => setF(x => ({ ...x, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
              </div>
            </div>
            <Button data-testid="save-debtor-btn" onClick={save} disabled={!f.name || !f.phone} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] mt-3">
              {editId ? "Update Debtor" : "Save Debtor"}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Customer Name</th>
              <th className="text-left p-3">Phone Number</th>
              <th className="text-left p-3">GST Number</th>
              <th className="text-right p-3">Outstanding Amount</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No debtor records yet</td></tr>}
            {pg.pageItems.map(c => {
              const active = (c.status || "Active") === "Active";
              return (
                <tr key={c.id} data-testid={`debtor-row-${c.id}`} className="border-t border-border hover:bg-secondary/50">
                  <td className="p-3 font-medium">{c.name}{c.contact_person && <span className="text-xs text-muted-foreground ml-2">({c.contact_person})</span>}</td>
                  <td className="p-3 font-mono text-xs">{c.phone}</td>
                  <td className="p-3 font-mono text-xs uppercase">{c.gst_number || "-"}</td>
                  <td className={`p-3 text-right font-mono ${outstanding[c.id] > 0 ? "text-destructive" : ""}`}>{inr(outstanding[c.id] || 0)}</td>
                  <td className="p-3"><span className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${active ? "border-green-500/40 bg-green-500/10 text-green-500" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>{c.status || "Active"}</span></td>
                  <td className="p-3 text-right">
                    <Button data-testid={`edit-debtor-${c.id}`} size="sm" variant="ghost" onClick={() => edit(c)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                    <Button data-testid={`del-debtor-${c.id}`} size="sm" variant="ghost" onClick={() => del(c.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
